import { httpClient } from 'app/api/http/http-client';

export type LoanFlowType = 'onboarding' | 'request_loan';

export const LOAN_FLOW_KEY = 'loanFlowType';

export const setLoanFlowType = (flow: LoanFlowType) => sessionStorage.setItem(LOAN_FLOW_KEY, flow);

export const getLoanFlowType = (): LoanFlowType =>
  (sessionStorage.getItem(LOAN_FLOW_KEY) as LoanFlowType) ?? 'onboarding';

export const getLoanEndpoint = (businessKey?: string): string => {
  const flow = getLoanFlowType();

  return businessKey
    ? `/api-web/v1/pub/${flow}?business_key=${businessKey}`
    : `/api-web/v1/pub/${flow}`;
};

export interface LoanInitResponse {
  business_key: string;
  result: string;
}

export interface LoanDetailsRequest {
  amount: string;
  term: string;
  rate: string;
  purpose: string;
  commission: string;
  monthly_payment: string;
}

export interface LoanDetailsResponse {
  business_key: string;
  result: string;
}

export const initLoanApplication = async (): Promise<LoanInitResponse> => {
  return await httpClient.get(getLoanEndpoint());
};

export const submitLoanDetails = async (
  body: LoanDetailsRequest,
  businessKey?: string
): Promise<LoanDetailsResponse> => {
  return await httpClient.post(getLoanEndpoint(businessKey), body);
};

export interface LoanIdentityRequest {
  industry_sector: string;
  curr_address: string;
  phone: string;
  email: string;
  serial_number: string;
}

export interface LoanIdentityResponse {
  business_key: string;
  result: string;
}

export const submitLoanIdentity = async (
  body: LoanIdentityRequest,
  businessKey: string
): Promise<LoanIdentityResponse> => {
  return await httpClient.post(getLoanEndpoint(businessKey), body);
};

export const submitLoanStep = async (
  businessKey: string,
  body: Record<string, unknown>
): Promise<{ business_key: string; result: string }> => {
  return await httpClient.post(getLoanEndpoint(businessKey), {
    business_key: businessKey,
    ...body,
  });
};

export interface LoanDocumentsRequest {
  bank_statement: string;
  income_declaration: string;
  vat_declaration?: string;
  dsmf_report?: string;
}

export interface LoanDocumentsResponse {
  business_key: string;
  ceo_name: string;
  company_name: string;
  tax_no: string;
  time: string;
  stage1_data?: {
    is_entrepreneur: string;
  };
  print_forms?: PrintFormsItem[];
}

export const submitLoanDocuments = async (
  body: LoanDocumentsRequest,
  businessKey: string
): Promise<LoanDocumentsResponse> => {
  return await httpClient.post(getLoanEndpoint(businessKey), body);
};

export interface PrintFormsItem {
  title: string;
  url: string;
}

export interface PrintFormsResponse {
  print_forms: PrintFormsItem[];
}

export const fetchPrintForms = async (businessKey: string): Promise<PrintFormsResponse> => {
  return await httpClient.post(getLoanEndpoint(businessKey), {});
};

export interface Stage1Response {
  business_key: string;
  result: string;
  stage1_data?: {
    amount: string;
    term: string;
    rate: string;
    purpose?: string;
    commission: string;
    monthly_payment: string;
    is_entrepreneur: string;
    purpose_text?: string;
  };
}

export interface Stage2Response {
  business_key: string;
  result: string;
  stage1_data?: Stage1Response['stage1_data'];
  stage2_data?: {
    industry_sector: string;
    curr_address: string;
    phone: string;
    email: string;
    serial_number: string;
  };
}

export interface Stage3Response {
  business_key: string;
  result: string;
  stage3_data?: {
    bank_statement: string;
    income_declaration: string;
    vat_declaration?: string;
    dsmf_report?: string;
    is_entrepreneur?: 'true' | 'false';
  };
}

export interface Stage4Response {
  business_key: string;
  result: string;
  stage1_data?: Stage1Response['stage1_data'];
  print_forms?: PrintFormsItem[];
}

export const fetchLoanStageData = async <T>(businessKey: string, stage: string): Promise<T> => {
  const flow = getLoanFlowType();

  return await httpClient.post(
    `/api-web/v1/pub/${flow}?business_key=${businessKey}&stage=${stage}`,
    { stage }
  );
};
